import { subscribeToNotifications } from '@/service/notificationsRealtime';
import type { NotificationsSubscription } from '@/service/notificationsRealtime';
import { supabase } from '@/service/supabaseClient';
import type {
  NotificationAction,
  NotificationQueryOptions,
  NotificationRow,
} from '@/types/notifications';
import { normalizeNotificationRow, normalizeNotificationRows } from '@/types/notifications';
import { TimeSync } from '@/utils/timesync/timesync';
import { defineStore } from 'pinia';

interface State {
  items: NotificationRow[];
  unreadCount: number;
  isLoaded: boolean;
  isSubscribing: boolean;
  channelName: string | null;
  subscription: NotificationsSubscription | null;
  popupEnabled: boolean;
  popupItem: NotificationRow | null;
  popupVisible: boolean;
  unreadAlarmKeys: Set<string>;
  /**
   * Gli id delle notifiche non lette, raggruppati per macchina
   * ("asset:<uuid>"). E cio che accende il pallino sulla scheda in flotta.
   *
   * Si tengono gli id e non un contatore perche lo stesso evento arriva da piu
   * strade - la lettura iniziale, il realtime, la marcatura come letta fatta
   * qui - e un contatore si sfaserebbe alla prima sovrapposizione. Un insieme
   * di id invece converge: aggiungere due volte lo stesso id non cambia niente.
   */
  unreadIdsByEntity: Record<string, Set<string>>;
}

/**
 * La chiave con cui una notifica viene attribuita a una macchina.
 *
 * Il device_id e' l'unico identificativo davvero univoco. **asset_id non lo e'**:
 * l'injector, per un dispositivo che nessuno ha configurato esplicitamente,
 * ripiega sull'asset "default" (vedi GetAssetID in injector/identity.go), quindi
 * piu macchine della stessa flotta possono condividere lo stesso asset_id. La
 * dashboard infatti identifica una macchina con la terna node/asset/device
 * dappertutto: qui non poteva essere diverso.
 *
 * Le notifiche scritte prima che la funzione mettesse device_id nel payload non
 * hanno di meglio dell'asset_id: restano attribuite all'asset, che e' tutta la
 * precisione che quella riga contiene.
 */
function notificationEntityKey(
  row: Pick<NotificationRow, 'entity_type' | 'entity_id' | 'data'>,
): string | null {
  if (!row.entity_id || row.entity_type === 'system') return null;

  const deviceId = (row.data as { device_id?: unknown } | null)?.device_id;
  if (typeof deviceId === 'string' && deviceId !== '') {
    return `${row.entity_type}:device:${deviceId}`;
  }
  return `${row.entity_type}:asset:${row.entity_id}`;
}

/**
 * Le chiavi da guardare per una macchina: quella precisa e quella per asset.
 *
 * Si sommano invece di sceglierne una, perche sono insiemi di righe diverse -
 * le nuove, che dicono quale macchina, e le vecchie, che dicono solo quale
 * asset. Una riga vecchia continua ad accendere tutte le macchine che
 * condividono quell'asset: e' cio' che quella riga sa, e fingere altrimenti
 * vorrebbe dire nascondere una notifica vera.
 */
function lookupKeys(
  entityType: 'asset' | 'companion',
  ids: { assetId?: string | null; deviceId?: string | null },
): string[] {
  const keys: string[] = [];
  if (ids.deviceId) keys.push(`${entityType}:device:${ids.deviceId}`);
  if (ids.assetId) keys.push(`${entityType}:asset:${ids.assetId}`);
  return keys;
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): State => ({
    items: [],
    unreadCount: 0,
    isLoaded: false,
    isSubscribing: false,
    channelName: null,
    subscription: null,
    popupEnabled: true,
    popupItem: null,
    popupVisible: false,
    unreadAlarmKeys: new Set<string>(),
    unreadIdsByEntity: {},
  }),
  actions: {
    async init(userId: string): Promise<void> {
      // Fetch first page
      await this.getAll({ page: 1, limit: 20 });

      // Fetch unread count efficiently
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('is_read', false);
      if (!error && typeof count === 'number') {
        this.unreadCount = count;
      }

      // Una sola interrogazione per tutta la flotta. L'alternativa - una per
      // macchina, al montaggio di ogni scheda - moltiplica il traffico per il
      // numero di macchine e si ripete a ogni cambio di pagina.
      await this.refreshUnreadByEntity();
      this.isLoaded = true;
    },

    /**
     * Ricostruisce da zero la mappa delle non lette per macchina.
     *
     * Legge solo le colonne che servono a raggrupparle, non i corpi: e una
     * riga magra per notifica non letta, e le non lette di un utente sono
     * poche per definizione - quando diventano tante, e perche qualcuno deve
     * guardarle.
     */
    async refreshUnreadByEntity(): Promise<void> {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, entity_type, entity_id, data')
        .eq('is_read', false)
        .not('entity_id', 'is', null)
        .limit(1000);
      if (error || !data) return;

      const next: Record<string, Set<string>> = {};
      for (const row of data as Array<
        Pick<NotificationRow, 'id' | 'entity_type' | 'entity_id' | 'data'>
      >) {
        const key = notificationEntityKey(row);
        if (!key) continue;
        const ids = next[key] ?? new Set<string>();
        ids.add(row.id);
        next[key] = ids;
      }
      this.unreadIdsByEntity = next;
    },

    /**
     * Segna una notifica come letta o non letta nella mappa per macchina.
     * Idempotente: chiamarla due volte per lo stesso id non sposta niente.
     */
    trackEntityUnread(
      row: Pick<NotificationRow, 'id' | 'entity_type' | 'entity_id' | 'data' | 'is_read'>,
      unread: boolean,
    ): void {
      const key = notificationEntityKey(row);
      if (!key) return;
      const ids = new Set(this.unreadIdsByEntity[key] ?? []);
      if (unread) ids.add(row.id);
      else ids.delete(row.id);

      if (ids.size === 0) {
        const { [key]: _removed, ...rest } = this.unreadIdsByEntity;
        this.unreadIdsByEntity = rest;
        return;
      }
      this.unreadIdsByEntity = { ...this.unreadIdsByEntity, [key]: ids };
    },

    /**
     * Quante notifiche non lette ha questa macchina: allarmi, anomalie e
     * notizie insieme. E il numero nel pallino sulla scheda.
     */
    unreadCountFor(
      entityType: 'asset' | 'companion',
      ids: { assetId?: string | null; deviceId?: string | null },
    ): number {
      return lookupKeys(entityType, ids).reduce(
        (total, key) => total + (this.unreadIdsByEntity[key]?.size ?? 0),
        0,
      );
    },

    async getAll(options: NotificationQueryOptions = {}): Promise<NotificationRow[]> {
      const page = options.page ?? 1;
      const limit = options.limit ?? 20;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to);

      if (options.onlyUnread) {
        query = query.eq('is_read', false);
      }

      if (options.search && options.search.trim().length > 0) {
        // Search by title or body, case-insensitive
        const term = options.search.trim();
        query = query.or(`title.ilike.%${term}%,body.ilike.%${term}%`);
      }

      const { data, error } = await query;
      if (error) {
        return this.items;
      }
      if (data) {
        const normalized = normalizeNotificationRows(
          (data as unknown as Array<{ data: unknown } & NotificationRow>) ?? [],
        );
        this.items = normalized as NotificationRow[];
        this.recalcUnreadCount();
      }
      return this.items;
    },

    async fetchPage(options: NotificationQueryOptions = {}): Promise<NotificationRow[]> {
      const page = options.page ?? 1;
      const limit = options.limit ?? 16;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to);

      if (options.onlyUnread) {
        query = query.eq('is_read', false);
      }
      if (options.search && options.search.trim().length > 0) {
        const term = options.search.trim();
        query = query.or(`title.ilike.%${term}%,body.ilike.%${term}%`);
      }

      const { data, error } = await query;
      if (error || !data) {
        return [];
      }
      const normalized = normalizeNotificationRows(
        (data as unknown as Array<{ data: unknown } & NotificationRow>) ?? [],
      );
      return normalized as NotificationRow[];
    },

    async markRead(id: string): Promise<void> {
      const nowIso = TimeSync.now().toISOString();
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: nowIso })
        .eq('id', id);
      if (!error) {
        const idx = this.items.findIndex((i) => i.id === id);
        if (idx !== -1) {
          const wasUnread = !this.items[idx].is_read;
          this.items[idx] = { ...this.items[idx], is_read: true, read_at: nowIso };
          this.trackEntityUnread(this.items[idx], false);
          if (wasUnread && this.unreadCount > 0) this.unreadCount -= 1;
        } else {
          if (this.unreadCount > 0) this.unreadCount -= 1;
        }
      }
    },

    async markUnread(id: string): Promise<void> {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: false, read_at: null })
        .eq('id', id);
      if (!error) {
        const idx = this.items.findIndex((i) => i.id === id);
        if (idx !== -1) {
          const wasUnread = !this.items[idx].is_read;
          this.items[idx] = { ...this.items[idx], is_read: false, read_at: null };
          this.trackEntityUnread(this.items[idx], true);
          if (!wasUnread) this.unreadCount += 1;
        } else {
          this.unreadCount += 1;
        }
      }
    },

    async remove(id: string): Promise<void> {
      const { error } = await supabase.from('notifications').delete().eq('id', id);
      if (!error) {
        const removed = this.items.find((i) => i.id === id);
        const wasUnread = removed?.is_read === false;
        if (removed) this.trackEntityUnread(removed, false);
        this.removeLocal(id);
        if (wasUnread && this.unreadCount > 0) this.unreadCount -= 1;
      }
    },

    upsertLocal(row: NotificationRow): void {
      const normalized = normalizeNotificationRow(
        row as unknown as { data: unknown } & NotificationRow,
      ) as NotificationRow;
      const idx = this.items.findIndex((i) => i.id === normalized.id);
      if (idx === -1) {
        this.items.unshift(normalized);
      } else {
        this.items[idx] = { ...this.items[idx], ...normalized };
      }
    },

    removeLocal(id: string): void {
      this.items = this.items.filter((i) => i.id !== id);
    },

    async subscribe(userId: string): Promise<void> {
      if (this.subscription) {
        await this.unsubscribe();
      }
      this.isSubscribing = true;
      const sub = subscribeToNotifications(userId, {
        onInsert: (row) => {
          this.upsertLocal(row);
          if (!row.is_read) this.unreadCount += 1;
          if (this.popupEnabled) {
            this.popupItem = row;
            this.popupVisible = true;
          }
          if (row.kind === 'alarm' && !row.is_read) {
            const key = notificationEntityKey(row);
            if (key) this.unreadAlarmKeys.add(key);
          }
          // Il pallino sulla scheda conta TUTTE le non lette, non solo gli
          // allarmi: un'anomalia e una notizia di fine missione sono entrambe
          // roba da leggere, e chi guarda la flotta vuole sapere che c'e'
          // qualcosa, non di che tipo sia.
          this.trackEntityUnread(row, !row.is_read);
        },
        onUpdate: (row) => {
          this.upsertLocal(row);
          this.recalcUnreadCount();
          if (row.kind === 'alarm') {
            const key = notificationEntityKey(row);
            if (key) {
              if (row.is_read) this.unreadAlarmKeys.delete(key);
              else this.unreadAlarmKeys.add(key);
            }
          }
          this.trackEntityUnread(row, !row.is_read);
        },
        onDelete: (row) => {
          this.removeLocal(row.id);
          this.recalcUnreadCount();
          if (row.kind === 'alarm') {
            const key = notificationEntityKey(row);
            if (key) this.unreadAlarmKeys.delete(key);
          }
          this.trackEntityUnread(row, false);
        },
        onStatusChange: (status) => {
          if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            this.isSubscribing = false;
          }
        },
      });
      this.subscription = sub;
      this.channelName = sub.channelName;
    },

    async unsubscribe(): Promise<void> {
      if (this.subscription) {
        await this.subscription.unsubscribe();
      }
      this.subscription = null;
      this.isSubscribing = false;
      this.channelName = null;
    },

    recalcUnreadCount(): void {
      this.unreadCount = this.items.reduce((acc, it) => acc + (it.is_read ? 0 : 1), 0);
    },

    hasUnreadAlarm(
      entityType: 'asset' | 'companion',
      ids: { assetId?: string | null; deviceId?: string | null },
    ): boolean {
      return lookupKeys(entityType, ids).some((key) => this.unreadAlarmKeys.has(key));
    },

    /**
     * Returns the total number of notifications in the database.
     * Uses an efficient HEAD count query.
     */
    async countTotal(): Promise<number> {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true });
      if (error || typeof count !== 'number') {
        return this.items.length;
      }
      return count;
    },

    /**
     * Returns the total number of unread notifications and updates store state.
     */
    async countUnread(): Promise<number> {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('is_read', false);
      if (!error && typeof count === 'number') {
        this.unreadCount = count;
        return count;
      }
      // Fallback to local calculation
      const local = this.items.reduce((acc, it) => acc + (it.is_read ? 0 : 1), 0);
      this.unreadCount = local;
      return local;
    },

    /**
     * Marks all notifications as read in the database and updates local state.
     *
     * Reports whether the database agreed. It used to swallow the failure and
     * return as if nothing had happened, which the interface then read as
     * success - see clearAll just below, where that habit had a real cost.
     */
    async markAllRead(): Promise<boolean> {
      const nowIso = TimeSync.now().toISOString();
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: nowIso })
        .eq('is_read', false);
      if (error) {
        console.error('[notifications] cannot mark all as read', error);
        return false;
      }
      this.items = this.items.map((it) => ({ ...it, is_read: true, read_at: nowIso }));
      this.unreadCount = 0;
      this.unreadAlarmKeys = new Set<string>();
      this.unreadIdsByEntity = {};
      return true;
    },

    /**
     * Deletes every notification the user can see, reporting whether it worked.
     *
     * The filter used to be `neq('id', '')`, meant as "every row". Postgres
     * never saw it that way: `id` is not text, so the empty string had to be
     * cast to its type and the cast failed. Every single emptying came back as
     * an error - and the error was swallowed here and the list cleared on
     * screen anyway, so the notifications looked gone until the next reload
     * brought them all back.
     *
     * `not('id', 'is', null)` is the same intent without a value to cast: it
     * matches every row whatever the column's type is.
     *
     * Two things are checked before declaring success. The error, obviously.
     * And the number of rows that actually came back deleted: a missing RLS
     * policy deletes nothing and reports no error at all, which is the same
     * silent lie wearing different clothes. Rows on screen and nothing deleted
     * means it did not happen.
     */
    async clearAll(): Promise<boolean> {
      const hadItems = this.items.length > 0;
      const { data, error } = await supabase
        .from('notifications')
        .delete()
        .not('id', 'is', null)
        .select('id');

      if (error) {
        console.error('[notifications] cannot empty the list', error);
        return false;
      }

      const deleted = data?.length ?? 0;
      if (deleted === 0 && hadItems) {
        console.error(
          '[notifications] the delete removed no rows: check the RLS delete policy on notifications',
        );
        return false;
      }

      this.items = [];
      this.unreadCount = 0;
      this.unreadAlarmKeys = new Set<string>();
      this.unreadIdsByEntity = {};
      return true;
    },
  },
});
